import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Table,
  Text,
  VStack,
  useDisclosure,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Tbody, Td, Th, Thead, Tr, Switch} from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { adminAPI } from '../services/api';

const PLAN_ORDER = ['GRATIS', 'ESTANDAR', 'INTERMEDIO', 'AVANZADO', 'COFRADIA'];

const FEATURE_LABELS = {
  perfilPublico: 'Perfil público',
  comunidad: 'Comunidad',
  tuWeb: 'Tu Web',
  dominioPropio: 'Dominio propio',
  calendario: 'Calendario',
  eventos: 'Eventos',
  contactos: 'Contactos',
  mensajeria: 'Mensajería',
  acuerdos: 'Acuerdos',
  archivoMusical: 'Archivo musical',
  musicos: 'Mis músicos',
  formaciones: 'Formaciones',
  actuaciones: 'Actuaciones',
  ensayos: 'Ensayos',
  documentos: 'Documentos',
  estadisticas: 'Estadísticas',
  objetivos: 'Objetivos',
  encuestas: 'Encuestas',
  permisos: 'Permisos',
};

const QUOTA_LABELS = {
  eventosPorMes: 'Eventos por mes',
  contactosMax: 'Contactos máximos',
  archivoMusicalMaxObras: 'Obras en archivo',
  archivoMusicalMaxPaginas: 'Páginas en archivo',
  almacenamientoMaxMb: 'Almacenamiento (MB)',
};

const clonePlan = (plan) => ({
  tier: plan.tier,
  label: plan.label,
  budgetEur: plan.budgetEur,
  features: { ...(plan.features || {}) },
  quotas: { ...(plan.quotas || {}) },
});

const quotasEqual = (a, b, keys) =>
  keys.every((k) => (a?.[k] ?? null) === (b?.[k] ?? null));

const AdminPlanesCatalog = () => {
  
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(null);
  const [featureKeys, setFeatureKeys] = useState([]);
  const [quotaKeys, setQuotaKeys] = useState([]);
  const [plans, setPlans] = useState({});
  const [drafts, setDrafts] = useState({});
  const [overlays, setOverlays] = useState({});
  const [userCounts, setUserCounts] = useState({});
  const [pending, setPending] = useState(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPlanCatalog();
      const data = res.data || {};
      const keys = data.featureKeys?.length
        ? data.featureKeys
        : Object.keys(FEATURE_LABELS);
      const qKeys = data.quotaKeys?.length
        ? data.quotaKeys
        : Object.keys(QUOTA_LABELS);
      const nextPlans = {};
      const nextDrafts = {};
      for (const p of data.plans || []) {
        nextPlans[p.tier] = clonePlan(p);
        nextDrafts[p.tier] = clonePlan(p);
      }
      setFeatureKeys(keys);
      setQuotaKeys(qKeys);
      setPlans(nextPlans);
      setDrafts(nextDrafts);
      setOverlays(data.overlays || {});
      setUserCounts(data.userCounts || {});
    } catch {
      toaster.create({ title: 'No se pudo cargar el catálogo', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const tiers = useMemo(
    () => PLAN_ORDER.filter((t) => drafts[t]),
    [drafts],
  );

  const isDirty = (tier) => {
    const a = drafts[tier];
    const b = plans[tier];
    if (!a || !b) return false;
    if (a.label !== b.label) return true;
    if (Number(a.budgetEur) !== Number(b.budgetEur)) return true;
    if (featureKeys.some((k) => Boolean(a.features[k]) !== Boolean(b.features[k]))) {
      return true;
    }
    return !quotasEqual(a.quotas, b.quotas, quotaKeys);
  };

  const setFeature = (tier, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        features: { ...prev[tier].features, [key]: value },
      },
    }));
  };

  const setQuota = (tier, key, raw) => {
    let next = null;
    if (raw === '') next = null;
    else {
      const n = Number(raw);
      next = Number.isFinite(n) && n >= 0 ? n : null;
    }
    setDrafts((prev) => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        quotas: { ...prev[tier].quotas, [key]: next },
      },
    }));
  };

  const setField = (tier, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], [field]: value },
    }));
  };

  const ask = (action, tier) => {
    setPending({ action, tier });
    onOpen();
  };

  const confirm = async () => {
    if (!pending) return;
    const { action, tier } = pending;
    const count = userCounts[tier] || 0;
    setSavingTier(tier);
    try {
      if (action === 'reset') {
        await adminAPI.resetPlanCatalog(tier);
        toaster.create({ title: 'Plan restablecido a los valores de código', type: 'success' });
      } else {
        const d = drafts[tier];
        await adminAPI.patchPlanCatalog(tier, {
          label: d.label,
          features: d.features,
          quotas: d.quotas,
          budgetEur: Number(d.budgetEur),
        });
        toaster.create({
          title: 'Catálogo actualizado',
          description: count
            ? `Afecta a ${count} cuenta${count === 1 ? '' : 's'} en este plan.`
            : undefined,
          type: 'success',
        });
      }
      onClose();
      setPending(null);
      await load();
    } catch {
      toaster.create({ title: 'No se pudo guardar el catálogo', type: 'error' });
    } finally {
      setSavingTier(null);
    }
  };

  if (loading) {
    return (
      <Box p={10} textAlign="center">
        <Spinner size="lg" color="purple.500" />
      </Box>
    );
  }

  const pendingCount = pending ? userCounts[pending.tier] || 0 : 0;

  return (
    <Box p={6}>
      <VStack align="stretch" gap={4}>
        <Text fontSize="sm" color="gray.600">
          Matriz de los planes existentes. Un cambio aplica en caliente a todas las
          cuentas de ese plan. Las excepciones por banda (pestaña Cuentas) siguen
          ganando. Vacío en una cuota = ilimitado.
        </Text>
        <Box overflowX="auto">
          <Table.Root size="sm" variant="simple">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader minW="180px">Campo</Table.ColumnHeader>
                {tiers.map((tier) => (
                  <Table.ColumnHeader key={tier} minW="150px">
                    <VStack align="start" gap={1}>
                      <Input
                        size="xs"
                        value={drafts[tier].label}
                        onChange={(e) => setField(tier, 'label', e.target.value)}
                      />
                      <HStack>
                        <Text fontSize="xs" color="gray.500">
                          {userCounts[tier] || 0} cuentas
                        </Text>
                        {overlays[tier] && (
                          <Badge colorPalette="purple" fontSize="0.55rem">
                            personalizado
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell fontWeight="600">Presupuesto (€ / ciclo)</Table.Cell>
                {tiers.map((tier) => (
                  <Table.Cell key={tier}>
                    <Input
                      size="xs"
                      type="number"
                      min={0}
                      step="0.01"
                      value={drafts[tier].budgetEur}
                      onChange={(e) =>
                        setField(tier, 'budgetEur', e.target.value)
                      }
                    />
                  </Table.Cell>
                ))}
              </Table.Row>
              {quotaKeys.map((key) => (
                <Table.Row key={key}>
                  <Table.Cell>{QUOTA_LABELS[key] || key}</Table.Cell>
                  {tiers.map((tier) => {
                    const val = drafts[tier].quotas[key];
                    return (
                      <Table.Cell key={tier}>
                        <Input
                          size="xs"
                          type="number"
                          min={0}
                          placeholder="Ilimitado"
                          value={val == null ? '' : val}
                          onChange={(e) => setQuota(tier, key, e.target.value)}
                        />
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}
              {featureKeys.map((key) => (
                <Table.Row key={key}>
                  <Table.Cell>{FEATURE_LABELS[key] || key}</Table.Cell>
                  {tiers.map((tier) => (
                    <Table.Cell key={tier}>
                      <Switch
                        size="sm"
                        colorPalette="purple"
                        checked={!!drafts[tier].features[key]}
                        onChange={(e) =>
                          setFeature(tier, key, e.target.checked)
                        }
                      />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
              <Table.Row>
                <Table.Cell />
                {tiers.map((tier) => (
                  <Table.Cell key={tier}>
                    <VStack align="stretch" gap={1}>
                      <Button
                        size="xs"
                        colorPalette="purple"
                        disabled={!isDirty(tier)}
                        loading={savingTier === tier}
                        onClick={() => ask('save', tier)}
                      >
                        Guardar
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!overlays[tier]}
                        loading={savingTier === tier}
                        onClick={() => ask('reset', tier)}
                      >
                        Restablecer
                      </Button>
                    </VStack>
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>
      </VStack>

      <Dialog.Root
        open={isOpen}
        initialFocusEl={() => cancelRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="md">
                  {pending?.action === 'reset'
                    ? 'Restablecer plan'
                    : 'Guardar catálogo'}
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm">
                    {pending?.action === 'reset'
                      ? 'Se quitan los ajustes de este plan y vuelven los valores de código.'
                      : 'Se actualiza el catálogo de este plan.'}{' '}
                    Afecta a <b>{pendingCount}</b> cuenta
                    {pendingCount === 1 ? '' : 's'} ahora mismo. No se resetea el
                    ciclo de suscripción.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} variant="ghost" mr={3} onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button
                    colorPalette={pending?.action === 'reset' ? 'red' : 'purple'}
                    onClick={confirm}
                    loading={!!savingTier}
                  >
                    Confirmar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Box>
  );
};

export default AdminPlanesCatalog;
